// ExecDormitory.js — "หอพัก" (pos 90/99)
//
// คำถามของหน้านี้: หอไหนแน่นหรือว่าง และทะเบียนหอพักครบทุกคนแล้วหรือยัง
//
// โรงเรียนเป็นแบบประจำทั้งหมด ไม่มีนักเรียนไปกลับ คนที่ไม่มีทะเบียนหอพักจึงคือ
// ข้อมูลที่ยังไม่ได้กรอก ไม่ใช่กลุ่มนักเรียนอีกแบบ — จึงเอามาเทียบผลลัพธ์กับ
// เด็กที่มีทะเบียนไม่ได้
import React, { useMemo } from "react";
import { Badge, Col, ProgressBar, Row, Table } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import {
  ExecPage, Panel, SectionTitle, StatCard, EmptyState, ScrollTable,
  YearPicker, useExecYear, useExecData,
  fmtNum, fmtPct, toNum, fmtThaiDate,
} from "./ExecShell";

export default function ExecDormitory() {
  const { years, yearId, setYearId } = useExecYear();
  const query = useMemo(() => ({ year_id: yearId }), [yearId]);
  const { data, loading, error } = useExecData("/api/executive/stats/dormitory", query);

  const occupancy = useMemo(() => (data?.occupancy || { available: false, villages: [], overall: {} }), [data]);
  const overall = occupancy.overall || {};
  const movements = useMemo(() => (data?.movements || { available: false, byMonth: [], totals: {} }), [data]);
  const registration = useMemo(() => (data?.registration || { available: false, byClass: [] }), [data]);
  const leaveWatch = data?.leaveWatch || { available: false, frequent: [], notReturned: [] };

  const villages = useMemo(() => (
    [...(occupancy.villages || [])].sort((a, b) => toNum(b.occupancyRate) - toNum(a.occupancyRate))
  ), [occupancy]);

  const movementOptions = useMemo(() => ({
    chart: { type: "bar", stacked: false, toolbar: { show: false } },
    colors: ["#198754", "#dc3545", "#0dcaf0"],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "56%" } },
    dataLabels: { enabled: false },
    legend: { position: "top", fontSize: "12px" },
    xaxis: { categories: movements.byMonth.map((m) => m.month), labels: { style: { fontSize: "10px" } } },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
    tooltip: { shared: true, intersect: false },
  }), [movements]);

  return (
    <ExecPage
      title="หอพัก"
      icon="ri-building-4-line"
      lead="อัตราเข้าพักรายเขต การย้ายเข้าออก และความครบของทะเบียนหอพัก"
      activeYear={data?.year}
      loading={loading}
      error={error}
      toolbar={<YearPicker years={years} yearId={yearId} onChange={setYearId} activeYear={data?.year} />}
    >
      {!occupancy.available ? (
        <EmptyState message="ยังไม่มีข้อมูลหอพักในระบบ" />
      ) : (
        <>
          <Row className="g-3 mb-4">
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-community-fill"
                color={toNum(overall.occupancyRate) >= 90 ? "danger" : toNum(overall.occupancyRate) >= 70 ? "warning" : "success"}
                label="อัตราเข้าพักรวม"
                value={fmtPct(overall.occupancyRate)}
                sub={`${fmtNum(overall.occupied)} จาก ${fmtNum(overall.capacity)} เตียง`}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-door-open-fill" color="info" label="เตียงว่าง"
                value={fmtNum(overall.vacancy)}
                sub={`กระจายอยู่ใน ${fmtNum(villages.length)} เขต`}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-home-2-fill" color="secondary" label="หอพักที่ยังไม่มีคนอยู่"
                value={fmtNum(overall.emptyBuildings)}
                sub={`จากหอที่เปิดใช้ ${fmtNum(overall.activeBuildings)} หลัง`}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-user-shared-fill"
                color={toNum(leaveWatch.notReturned?.length) > 0 ? "danger" : "success"}
                label="เด็กหอที่ยังไม่กลับ"
                value={fmtNum(leaveWatch.notReturned?.length)}
                sub="ออกไปแล้วเลยกำหนดกลับ"
                tone={toNum(leaveWatch.notReturned?.length) > 0 ? "danger" : undefined}
              />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xl={7}>
              <Panel title="อัตราเข้าพักรายเขต" note="เรียงจากแน่นที่สุด เขตชายและเขตหญิงใช้เลขซ้ำกันจึงแสดงชื่อเต็ม">
                <ScrollTable>
                  <Table className="table-sm mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>เขต</th>
                        <th className="text-end">หลัง</th>
                        <th className="text-end">เข้าพัก</th>
                        <th className="text-end">ความจุ</th>
                        <th style={{ minWidth: 130 }}>อัตราเข้าพัก</th>
                      </tr>
                    </thead>
                    <tbody>
                      {villages.map((v) => (
                        <tr key={v.village_id}>
                          <td className="fw-semibold">{v.village_name}</td>
                          <td className="text-end">{fmtNum(v.buildingCount)}</td>
                          <td className="text-end">{fmtNum(v.occupied)}</td>
                          <td className="text-end text-secondary">{fmtNum(v.capacity)}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <ProgressBar
                                className="flex-fill" style={{ height: 7 }}
                                now={toNum(v.occupancyRate)}
                                variant={toNum(v.occupancyRate) >= 90 ? "danger" : toNum(v.occupancyRate) >= 70 ? "warning" : "success"}
                              />
                              <span className="fs-xs" style={{ minWidth: 42 }}>{fmtPct(v.occupancyRate)}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </ScrollTable>
              </Panel>
            </Col>
            <Col xl={5}>
              <Panel title="หอที่แน่นที่สุด" note="ควรพิจารณาย้ายหรือขยายก่อนหลังอื่น">
                {(occupancy.fullest || []).length === 0 ? <EmptyState compact /> : occupancy.fullest.map((b) => (
                  <div key={b.dorm_id} className="d-flex align-items-center justify-content-between border-bottom py-2 fs-sm">
                    <span className="text-truncate">
                      {b.dorm_name}
                      <span className="text-secondary fs-xs d-block">{b.village_name}</span>
                    </span>
                    <Badge bg={toNum(b.occupancyRate) >= 100 ? "danger" : "warning"} className="flex-shrink-0">
                      {fmtNum(b.occupied)}/{fmtNum(b.capacity)}
                    </Badge>
                  </div>
                ))}
              </Panel>
            </Col>
          </Row>

          <SectionTitle
            icon="ri-clipboard-line"
            title="ความครบของทะเบียนหอพัก"
            note="โรงเรียนประจำ — นักเรียนทุกคนต้องมีที่พัก คนที่ยังไม่มีทะเบียนคือข้อมูลที่ขาด"
            color="primary"
          />
          <Row className="g-3 mb-4">
            <Col xl={7}>
              <Panel note="ไม่มีนักเรียนไปกลับ นักเรียนที่ยังไม่มีแถวหอพักคือคนที่ฝ่ายหอพักยังไม่ได้ลงทะเบียนเข้าระบบ">
                {!registration.available ? <EmptyState compact /> : (
                  <>
                    <Row className="g-3 mb-3">
                      <Col sm={4}>
                        <div className="border rounded p-3 text-center">
                          <div className="fs-xs text-secondary">นักเรียนทั้งหมด</div>
                          <div className="fw-semibold fs-4">{fmtNum(registration.total)}</div>
                        </div>
                      </Col>
                      <Col sm={4}>
                        <div className="border rounded p-3 text-center">
                          <div className="fs-xs text-secondary">มีทะเบียนหอพัก</div>
                          <div className="fw-semibold fs-4 text-success">{fmtNum(registration.allocated)}</div>
                        </div>
                      </Col>
                      <Col sm={4}>
                        <div className="border rounded p-3 text-center">
                          <div className="fs-xs text-secondary">ยังไม่ได้ลงทะเบียน</div>
                          <div className="fw-semibold fs-4 text-warning">{fmtNum(registration.missing)}</div>
                        </div>
                      </Col>
                    </Row>
                    <ScrollTable>
                      <Table className="table-sm mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>ระดับชั้น</th>
                            <th className="text-end">นักเรียน</th>
                            <th className="text-end">มีทะเบียน</th>
                            <th className="text-end">ยังไม่มี</th>
                            <th style={{ minWidth: 120 }}>ครบ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(registration.byClass || []).map((row) => (
                            <tr key={row.c_id}>
                              <td className="fw-semibold">{row.c_class}</td>
                              <td className="text-end">{fmtNum(row.total)}</td>
                              <td className="text-end">{fmtNum(row.allocated)}</td>
                              <td className="text-end fw-semibold text-warning">{fmtNum(row.missing)}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <ProgressBar
                                    className="flex-fill" style={{ height: 7 }}
                                    now={toNum(row.coverageRate)}
                                    variant={toNum(row.coverageRate) >= 90 ? "success" : toNum(row.coverageRate) >= 50 ? "warning" : "danger"}
                                  />
                                  <span className="fs-xs fw-semibold" style={{ minWidth: 42 }}>{fmtPct(row.coverageRate)}</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </ScrollTable>
                  </>
                )}
              </Panel>
            </Col>
            <Col xl={5}>
              <Panel title="การย้ายเข้า-ออก" note="รายเดือนตลอดปี">
                {!movements.available || movements.byMonth.length === 0 ? (
                  <EmptyState message="ยังไม่มีการเคลื่อนไหว" compact />
                ) : (
                  <>
                    <ReactApexChart
                      options={movementOptions}
                      series={[
                        { name: "ย้ายเข้า", data: movements.byMonth.map((m) => toNum(m.checkin)) },
                        { name: "ย้ายออก", data: movements.byMonth.map((m) => toNum(m.checkout)) },
                        { name: "ย้ายหลัง", data: movements.byMonth.map((m) => toNum(m.transfer)) },
                      ]}
                      type="bar" height={240}
                    />
                    <div className="d-flex gap-4 border-top pt-3 mt-2 fs-sm">
                      <span><span className="text-secondary">เข้า</span> <strong className="text-success">{fmtNum(movements.totals?.checkin)}</strong></span>
                      <span><span className="text-secondary">ออก</span> <strong className="text-danger">{fmtNum(movements.totals?.checkout)}</strong></span>
                      <span><span className="text-secondary">ย้ายหลัง</span> <strong className="text-info">{fmtNum(movements.totals?.transfer)}</strong></span>
                    </div>
                  </>
                )}
              </Panel>
            </Col>
          </Row>

          <SectionTitle icon="ri-home-heart-line" title="เด็กหอที่ต้องติดตาม" note="เรื่องที่หัวหน้าหอต้องตามโดยตรง" color="warning" />
          <Row className="g-3 mb-4">
            <Col xl={6}>
              <Panel title="ยังไม่กลับเข้าหอ" note="ออกไปแล้วเลยวันกลับที่ขออนุญาตไว้">
                {(leaveWatch.notReturned || []).length === 0 ? (
                  <EmptyState message="ไม่มีเด็กหอค้างกลับ" compact />
                ) : leaveWatch.notReturned.map((row) => (
                  <div key={`${row.st_id}-${row.return_date}`} className="d-flex align-items-center justify-content-between border-bottom py-2 fs-sm">
                    <span className="text-truncate">
                      {row.st_name}
                      <span className="text-secondary fs-xs d-block">{row.dorm_name} · กลับ {fmtThaiDate(row.return_date)}</span>
                    </span>
                    <Badge bg="danger" className="flex-shrink-0">เกิน {fmtNum(row.days_late)} วัน</Badge>
                  </div>
                ))}
              </Panel>
            </Col>
            <Col xl={6}>
              <Panel title="เด็กหอที่กลับบ้านบ่อยที่สุด" note="อยู่โรงเรียนน้อยกว่าที่ควร">
                {(leaveWatch.frequent || []).length === 0 ? <EmptyState compact /> : leaveWatch.frequent.map((row) => (
                  <div key={row.st_id} className="d-flex align-items-center justify-content-between border-bottom py-2 fs-sm">
                    <span className="text-truncate">
                      {row.st_name}
                      <span className="text-secondary fs-xs d-block">{row.dorm_name}</span>
                    </span>
                    <span className="flex-shrink-0">
                      <span className="fw-semibold">{fmtNum(row.times)} ครั้ง</span>
                      <span className="text-secondary fs-xs ms-2">{fmtNum(row.days)} วัน</span>
                    </span>
                  </div>
                ))}
              </Panel>
            </Col>
          </Row>
        </>
      )}
    </ExecPage>
  );
}
